import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaMicrophone,
  FaSync,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaTimes,
  FaEye,
  FaYoutube,
  FaExternalLinkAlt,
  FaTrophy,
  FaStar,
  FaHistory,
  FaDollarSign,
  FaRedo,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DescriptionViewer from "../../../Common/DescriptionViewer";
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const CompletedPaidConference = () => {
  const navigate = useNavigate();
  const [completedConferences, setCompletedConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    totalCompleted: 0,
    totalEarnings: 0,
    thisMonth: 0,
  });
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchCompletedConferences = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/completed/paid-conferences`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const conferences = response.data?.data || [];
      setCompletedConferences(conferences);

      const totalCompleted = conferences.length;
      const totalEarnings = conferences.reduce((sum, conf) => sum + (conf.earnings || conf.amountEarned || 0), 0);
      const thisMonth = conferences.filter(conf => {
        const completedDate = new Date(conf.completedAt || conf.proof?.approvedAt);
        const now = new Date();
        return completedDate.getMonth() === now.getMonth() && completedDate.getFullYear() === now.getFullYear();
      }).length;

      setStats({ totalCompleted, totalEarnings, thisMonth });
    } catch (err) {
      setError("Failed to fetch completed paid conferences");
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedConferences();
  }, []);

  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedConference(null);
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-emerald-500 to-green-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Archive Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Completed Tasks</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-emerald-600 italic leading-none">{stats.totalCompleted} Finalized</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchCompletedConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-emerald-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="paid" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {/* Statistics Grid - Ultra Compact */}
        {!loading && !error && (
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-900 rounded-2xl p-4 text-white relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-white/5 rounded-full blur-xl group-hover:bg-white/10 transition-all" />
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Total Pay</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-emerald-400">₹{stats.totalEarnings.toFixed(0)}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Missions</p>
              <p className="text-sm font-black text-slate-900 leading-none">{stats.totalCompleted}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Current</p>
              <p className="text-sm font-black text-blue-600 leading-none">{stats.thisMonth}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-emerald-100 border-t-emerald-500 rounded-full animate-spin" />
              <FaHistory className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-emerald-500 text-[8px]" />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Retrieving Archives...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Mission Code</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Deployment Date</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Net Earnings</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Finalized At</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Vault</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {completedConferences.map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center border border-emerald-100/50">
                          <FaCheckCircle className="text-emerald-600 text-[10px]" />
                        </div>
                        <span className="font-black text-slate-400 text-[10px] tabular-nums tracking-tighter italic">#{conference.conferenceId || "N/A"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-slate-700 italic">{conference.conferenceDetails?.conferenceDate || "N/A"}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-xs font-black text-emerald-600">₹{conference.earnings || conference.amountEarned || 0}</p>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums">
                      {conference.completedAt ? new Date(conference.completedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => handleViewDetails(conference)} className="h-8 px-4 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 mx-auto"><FaEye /> Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Record Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {completedConferences.length > 0 ? (
              completedConferences.map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-emerald-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-emerald-500 to-green-600 p-0.5 shadow-md shadow-emerald-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaTrophy className="text-emerald-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.conferenceDetails?.topic || "N/A"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widths tabular-nums italic">Record #{conference.conferenceId}</p>
                          <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800">Finalized</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Data Grid */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 text-xs"><FaDollarSign /></div>
                        <div>
                          <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Earned</p>
                          <p className="text-xs font-black text-slate-900 tracking-tighter leading-none tabular-nums">₹{conference.earnings || 0}</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 text-[10px]"><FaCalendarAlt /></div>
                        <div>
                          <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Verified</p>
                          <p className="text-[10px] font-black text-slate-800 leading-none tabular-nums italic">{new Date(conference.completedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                        </div>
                      </div>
                    </div>

                    {/* Single Action */}
                    <button onClick={() => handleViewDetails(conference)} className="w-full h-11 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest"><FaEye /> View Official Report</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaHistory className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">Archive Empty</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No completed paid projects found in official records.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Official Record Modal - Premium Compact */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-6 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-black tracking-tighter leading-tight mb-1">Official Mission Report</h3>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-emerald-100 italic">Historical data & payout verification</p>
                </div>
                <button onClick={handleCloseModal} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-all"><FaTimes className="text-xs" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Financial Intel */}
              <div className="bg-emerald-50/50 rounded-2xl p-5 border border-emerald-100 flex items-center justify-between">
                <div>
                  <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mb-1">Final Settlement</p>
                  <p className="text-2xl font-black text-emerald-700 tabular-nums">₹{selectedConference.earnings || 0}</p>
                </div>
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100"><FaDollarSign /></div>
              </div>

              {/* Basic Intel */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Topic</p>
                  <p className="text-[11px] font-black text-slate-800 leading-tight">{selectedConference.conferenceDetails?.topic || "N/A"}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Time Code</p>
                  <p className="text-[11px] font-black text-slate-800 tabular-nums italic">{selectedConference.conferenceDetails?.conferenceDate}</p>
                </div>
              </div>

              {/* Location Intel */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Deployment Area</p>
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-rose-500 text-xs" />
                  <p className="text-[11px] font-black text-slate-900 uppercase tracking-tight truncate">{selectedConference.conferenceDetails?.place}, {selectedConference.conferenceDetails?.city}</p>
                </div>
              </div>

              {/* Coverage Proof */}
              <div className="bg-blue-50/30 rounded-2xl p-4 border border-blue-100/50 space-y-3">
                <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Digital Footprint (Coverage Proof)</p>

                <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-blue-50 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FaYoutube className="text-rose-600" />
                    <div>
                      <p className="text-[10px] font-black text-slate-800 leading-none mb-1">{selectedConference.proof?.channelName || "Official Portal"}</p>
                      <p className="text-[9px] font-bold text-slate-400 leading-none">{selectedConference.proof?.platform}</p>
                    </div>
                  </div>
                  {selectedConference.proof?.videoLink && (
                    <a href={selectedConference.proof.videoLink} target="_blank" rel="noopener noreferrer" className="h-8 px-4 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5"><FaExternalLinkAlt className="text-[8px]" /> View Coverage</a>
                  )}
                </div>

                {(selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot) && (
                  <div className="relative group">
                    <img
                      src={(selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot).startsWith('http')
                        ? (selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot)
                        : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${(selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot).replace('\\', '/')}`}
                      alt="Record Screenshot"
                      className="w-full h-24 object-cover rounded-xl border border-white shadow-lg grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 rounded-xl transition-all">
                      <a href={(selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot).startsWith('http')
                        ? (selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot)
                        : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${(selectedConference.proof?.screenshot || selectedConference.proofDetails?.screenshot).replace('\\', '/')}`} target="_blank" rel="noreferrer" className="text-white text-xs font-black uppercase tracking-widest bg-emerald-600 px-4 py-2 rounded-lg">Zoom Evidence</a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-slate-50 flex items-center justify-center px-6 border-t border-slate-100/50">
              <button onClick={handleCloseModal} className="h-10 w-full bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-slate-200">Close Official Record</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default CompletedPaidConference;
